// src/pages/CrimePreventionPage.jsx
import { useEffect, useMemo, useState } from "react";
import { Banner } from "../../../components/common/Banner";
import * as Icons from "lucide-react";
import {
  getCPConfig,
  getCPContent,
  getLatestCPDoc,
  downloadCPDocUrl,
} from "../../../services/crimePreventionService";

/* ---------- helpers ---------- */
const absoluteUrl = (href) => {
  try {
    return new URL(href, window.location.origin).href;
  } catch {
    return href;
  }
};

/* ---------- UI ---------- */
const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-[#0B4A8F] border border-blue-100 px-3 py-1 text-sm font-medium">
    {children}
  </span>
);
const Dot = () => <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-300" />;

/** Miniatura con fallback: intenta coverUrl; si no, embebe la 1ª página del PDF (solo si hay secureUrl) */
function CoverPreview({ coverSrc, pdfHref, alt = "Portada", size = "sm" }) {
  const [imgOk, setImgOk] = useState(Boolean(coverSrc));

  // Tamaños predefinidos
  const sizeCls =
    size === "xs"
      ? "w-[80px] h-[112px] sm:w-[100px] sm:h-[140px]"
      : size === "sm"
      ? "w-[120px] h-[160px] sm:w-[140px] sm:h-[190px] md:w-[160px] md:h-[220px]"
      : size === "md"
      ? "w-[140px] h-[190px] sm:w-[160px] sm:h-[220px] md:w-[180px] md:h-[250px]"
      : "w-[160px] h-[220px] sm:w-[180px] sm:h-[250px] md:w-[200px] md:h-[280px]"; // lg

  const canEmbed = Boolean(pdfHref);

  return (
    <div
      className={`${sizeCls} rounded-xl border border-blue-100 bg-white overflow-hidden flex items-center justify-center shadow-sm`}
    >
      {imgOk && coverSrc ? (
        <img
          src={absoluteUrl(coverSrc)}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setImgOk(false)}
          loading="lazy"
        />
      ) : canEmbed ? (
        <embed
          src={`${absoluteUrl(pdfHref)}#page=1&zoom=105`}
          type="application/pdf"
          className="w-full h-full block"
        />
      ) : null}
    </div>
  );
}


/* ---------- Página ---------- */
export default function CrimePreventionPage() {
  const [pageTitle, setPageTitle] = useState("Modelo de Prevención del Delito");
  const [subtitle, setSubtitle] = useState(
    "Compromiso de cumplimiento, prevención de delitos y canales de reporte."
  );
  const [lastReview, setLastReview] = useState("");
  const [principlesVisible, setPrinciplesVisible] = useState(true);
  const [principles, setPrinciples] = useState([]);
  const [sections, setSections] = useState([]);
  const [latestDoc, setLatestDoc] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getCPConfig();
        if (cfg?.pageTitle) setPageTitle(String(cfg.pageTitle));
        setSubtitle(cfg?.subtitle ? String(cfg.subtitle) : "Compromiso de cumplimiento, prevención de delitos y canales de reporte.");
        if (cfg?.lastReview) setLastReview(String(cfg.lastReview));
        setPrinciplesVisible(cfg?.principlesVisible !== undefined ? !!cfg.principlesVisible : true);

        const c = await getCPContent();
        if (c?.principles?.length) setPrinciples(c.principles);
        if (c?.sections?.length) setSections(c.sections);
        if (c?.lastReview && !cfg?.lastReview) setLastReview(String(c.lastReview));

        const latest = await getLatestCPDoc().catch(() => null);
        if (latest?.id) setLatestDoc(latest);
      } catch {
        /* defaults */
      }
    })();
  }, []);

  // Primer CTA de alguna sección (si existe)
  const globalCTA = useMemo(() => {
    for (const s of sections || []) {
      if (s?.cta?.reportUrl || s?.cta?.policyUrl) return s.cta;
    }
    return null;
  }, [sections]);

  // HREF para visualizar/embeber (NUNCA usar /download para <embed>, evita 401)
  const viewHref = latestDoc?.secureUrl?.trim() ? latestDoc.secureUrl : null;
  // HREF para el botón (prefiere secureUrl; sino usa /download)
  const buttonHref =
    viewHref ?? (latestDoc?.id ? downloadCPDocUrl(latestDoc.id) : null);

  const coverUrl =
    latestDoc?.coverUrl && String(latestDoc.coverUrl).trim().length > 0
      ? latestDoc.coverUrl
      : null;

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <Banner title={pageTitle} message={subtitle} className="mb-6" />

      {/* Badges de contexto */}
      <div className="flex flex-wrap items-center gap-2 text-sm mb-6">
        {lastReview && <Pill>Última revisión: {lastReview}</Pill>}
        {latestDoc?.versionTag && (
          <>
            <Dot />
            <Pill>Versión PDF: {latestDoc.versionTag}</Pill>
          </>
        )}
      </div>

      {/* HERO: portada + título + botón */}
      {buttonHref && (
        <div className="rounded-3xl bg-gradient-to-t from-[#0EC5DF] via-[#0488AF] to-[#0B4A8F] text-white px-5 py-6 md:px-7 md:py-7 mb-10 shadow-sm">
          <div className="grid gap-5 md:gap-7 md:grid-cols-[minmax(240px,280px),minmax(0,1fr)] items-start">
            {/* Portada */}
            <div className="w-full max-w-[280px] justify-self-center md:justify-self-start">
              <div className="aspect-[3/4] rounded-2xl bg-white overflow-hidden shadow-md">
                {coverUrl ? (
                  <img
                    className="w-full h-full object-cover"
                    src={coverUrl}
                    alt="Portada Modelo de Prevención del Delito"
                    onError={(e) => {
                      if (viewHref) {
                        e.currentTarget.parentElement.innerHTML = `<embed type="application/pdf" src="${absoluteUrl(viewHref)}#page=1&zoom=125" style="width:100%;height:100%;display:block;border:0" />`;
                      }
                    }}
                  />
                ) : viewHref ? (
                  <embed
                    type="application/pdf"
                    src={`${absoluteUrl(viewHref)}#page=1&zoom=125`}
                    style={{ width: '100%', height: '100%', display: 'block', border: 0 }}
                  />
                ) : null}
              </div>
            </div>

            {/* Título + botón */}
            <div className="w-full min-w-0">
              <div className="text-xl md:text-2xl font-semibold mb-3 text-white">
                {latestDoc?.title?.trim() || "Documento oficial del Modelo"}
              </div>
              <a
                href={absoluteUrl(buttonHref)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/95 text-[#0488AF] hover:bg-white font-medium shadow-sm"
              >
                <Icons.FileDown className="w-4 h-4" />
                Ver / Descargar Modelo de Prevención del Delito
              </a>
            </div>
          </div>
        </div>
      )}

      {/* PRINCIPIOS */}
      {principlesVisible && principles?.length > 0 && (
        <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5 mb-8">
          <div className="text-sm font-semibold text-gray-700 mb-3">
            Principios del Modelo
          </div>
          <div className="flex flex-wrap gap-2">
            {principles.map((p, i) => (
              <Pill key={i}>
                <Icons.CheckCircle2 className="w-4 h-4" />
                {p}
              </Pill>
            ))}
          </div>
        </div>
      )}

      {/* SECCIONES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(sections && sections.length > 0
          ? sections
          : [
              {
                title: "Gobernanza y alcance",
                icon: "ShieldCheck",
                body: [
                  "Define roles y responsabilidades del Directorio, gerencias y colaboradores.",
                  "Aplica a todas las operaciones, filiales y terceros relevantes.",
                ],
              },
              {
                title: "Mapa de riesgos y controles",
                icon: "GanttChart",
                bullets: [
                  "Identificación y evaluación periódica de riesgos.",
                  "Controles preventivos y detectivos documentados.",
                  "Monitoreo y mejora continua.",
                ],
              },
              {
                title: "Canales de integridad (denuncias y consultas)",
                icon: "Megaphone",
                cta: {
                  reportUrl: "mailto:integridad@empresa.cl",
                  policyUrl: "/politicas/canal-de-integridad",
                },
              },
              {
                title: "Capacitación y cultura",
                icon: "GraduationCap",
                body: [
                  "Programas de inducción y refuerzo anual.",
                  "Registro de asistencia y evaluación de efectividad.",
                ],
              },
            ]).map((sec, idx) => {
          const Icon = (sec.icon && Icons[sec.icon]) || Icons.FileText;
          const hasBody = Array.isArray(sec.body) && sec.body.length > 0;
          const hasBullets = Array.isArray(sec.bullets) && sec.bullets.length > 0;

          return (
            <section key={idx} className="rounded-2xl overflow-hidden bg-white ring-1 ring-black/5">
              <div className="px-5 py-4 bg-gradient-to-r from-[#E6F0FF] to-[#E6FBFF] flex items-center gap-3">
                {sec.iconImageUrl ? (
                  <img
                    src={sec.iconImageUrl}
                    alt=""
                    className="w-6 h-6 rounded-md object-contain border border-blue-100 bg-white"
                  />
                ) : (
                  <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                    <Icon className="w-4 h-4 text-[#0B4A8F]" />
                  </div>
                )}
                <h2 className="text-base md:text-lg font-semibold text-gray-900">{sec.title}</h2>
              </div>

              <div className="px-5 py-4 text-gray-700 leading-relaxed">
                {hasBody &&
                  sec.body.map((p, i) => (
                    <p key={i} className="mb-2">
                      {p}
                    </p>
                  ))}
                {hasBullets && (
                  <ul className="list-disc pl-6 space-y-1">
                    {sec.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
                {!hasBody && !hasBullets && (
                  <div className="text-sm text-gray-500">Contenido próximamente.</div>
                )}
              </div>

              {sec.cta && (sec.cta.reportUrl || sec.cta.policyUrl || sec.cta.contactUrl) && (
                <div className="px-5 pb-4 flex flex-wrap gap-2">
                  {sec.cta.reportUrl && (
                    <a
                      href={sec.cta.reportUrl}
                      target={
                        sec.cta.reportUrl.startsWith("/") ||
                        /^mailto:/i.test(sec.cta.reportUrl)
                          ? "_self"
                          : "_blank"
                      }
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98]"
                    >
                      <Icons.Megaphone className="w-4 h-4" />
                      Reportar una inquietud
                    </a>
                  )}
                  {sec.cta.policyUrl && (
                    <a
                      href={sec.cta.policyUrl}
                      target={
                        sec.cta.policyUrl.startsWith("/") ? "_self" : "_blank"
                      }
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
                    >
                      <Icons.ExternalLink className="w-4 h-4" />
                      Ver política
                    </a>
                  )}
                  {sec.cta.contactUrl && (
                    <a
                      href={sec.cta.contactUrl}
                      target={
                        sec.cta.contactUrl.startsWith("/") ||
                        /^mailto:/i.test(sec.cta.contactUrl)
                          ? "_self"
                          : "_blank"
                      }
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98]"
                    >
                      {/^mailto:/i.test(sec.cta.contactUrl) ? (
                        <Icons.Mail className="w-4 h-4" />
                      ) : (
                        <Icons.ExternalLink className="w-4 h-4" />
                      )}
                      Contacto
                    </a>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
